import { isEmailMessage } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message/email";
import type { ConversationThreadResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { useThreadMessages } from "../hooks/use-conversation-data";
import { useEmailThreadView } from "../hooks/use-email-thread-view";
import { EmailMessageCard } from "./email-message-card";
import { ThreadHeader } from "./thread-header";

/**
 * Email thread view component.
 * Fetches its own messages via useThreadMessages and uses useEmailThreadView
 * for display state management.
 */
export const EmailThreadView = memo(function EmailThreadView({
  thread,
}: {
  thread: ConversationThreadResponse;
}) {
  // Fetch messages for this thread from the collection (local reactive query)
  const { data: messages } = useThreadMessages(thread.id);

  // Filter to email messages only
  const emailMessages = useMemo(() => {
    return messages.filter((m) => isEmailMessage(m));
  }, [messages]);

  // Use the email thread view hook for formatting and expansion state
  const { formattedMessages, isMessageExpanded, toggleMessageExpanded } =
    useEmailThreadView(emailMessages);

  // Extract subject from thread channel data if available
  const threadSubject =
    thread.channelData?._tag === "email"
      ? (thread.channelData as { subject?: string | null }).subject
      : null;

  if (formattedMessages.length === 0) {
    return (
      <Flex align="center" dir="column" gap="md" p="xl">
        <Text fontSize="md" textColor="secondary">
          No messages in this thread
        </Text>
      </Flex>
    );
  }

  return (
    <Flex dir="column" gap="none">
      <ThreadHeader channel="email" subject={threadSubject} />
      {formattedMessages.map((message) => (
        <EmailMessageCard
          isExpanded={isMessageExpanded(message.id)}
          key={message.id}
          message={message}
          onToggleExpand={() => toggleMessageExpanded(message.id)}
        />
      ))}
    </Flex>
  );
});
